import ks from "../ReUsableTS/kBrowserShared";
import { khtmlButton } from "../ReUsableTS/kHTML";
import LNG from "./kLNG";
import type { kModalDialog } from "./kModalDialog";

/**
 * Member bodies of kModalDialog moved out of kModalDialog.ts (size split, 2026-10-20). kModalDialog keeps every
 * member with the same signature as a one-line delegate here; private state is reached by element access
 * (Dlg["_X"]), and the class itself is passed in (Cls) for its static state, so there is no runtime import
 * back to kModalDialog.ts.
 */
export class kModalDialogButtons {
    /** Body of kModalDialog._GetButtonRow (moved verbatim; the dialog is Dlg). */
    public static _GetButtonRow(Dlg: kModalDialog): HTMLDivElement {
        if (ks.KErrorMode && ks.KShowingErrorDialog === false) { return document.createElement("div"); }
        try {
            if (Dlg["_ButtonRow"] !== null) { return Dlg["_ButtonRow"]; }
            Dlg["_ButtonRow"] = document.createElement("div");
            Dlg["_ButtonRow"].style.display = "flex";
            Dlg["_ButtonRow"].style.justifyContent = "flex-end";
            Dlg["_ButtonRow"].style.gap = "8px";
            Dlg["_ButtonRow"].style.marginTop = "12px";
            Dlg.DialogContent.appendChild(Dlg["_ButtonRow"]);
            return Dlg["_ButtonRow"];
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kModalDialogButtons._GetButtonRow");
            return document.createElement("div");
        }
    }
    /** Body of kModalDialog.AddCancelButton (moved verbatim; the dialog is Dlg). */
    public static AddCancelButton(Dlg: kModalDialog, Callback?: any): void {
        if (ks.KErrorMode && ks.KShowingErrorDialog === false) { return; }
        try {
            // Idempotent: the button may already have been built by the
            // constructor (when showCancelButton is passed there) — Show()
            // re-runs the lazy build, so without this guard the dialog gets
            // TWO Cancel buttons. Refresh the callback and bail.
            if (Dlg.CancelButton !== null) {
                if (Callback !== undefined) { Dlg.OnCancelCB = Callback; }
                return;
            }
            // Empty class name is treated by khtmlButton exactly like an unset one, so the
            // default (ButtonsUseDefaultStyle=true, CancelButtonClassName="") is unchanged.
            const Cancel: khtmlButton = new khtmlButton(
                "kDialogCancelButton",
                Dlg.CancelButtonClassName,
                Dlg.ButtonsUseDefaultStyle
            );
            Dlg.CancelButton = Cancel;
            if (Cancel.Button) {
                if (Dlg.ButtonsUseDefaultStyle === true) {
                    Cancel.SetSecondary();
                }
                const Row: HTMLDivElement = Dlg["_GetButtonRow"]();
                Row.appendChild(Cancel.Button);
                Dlg.OnCancelCB = Callback;
                if (Dlg.CancelButtonCaption) {
                    Cancel.SetText(Dlg.CancelButtonCaption);
                } else {
                    Cancel.SetText(LNG.CANCEL);
                }
                Cancel.SetCallBack(Dlg["_CancelClick"]);
            }
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogButtons.AddCancelButton"); }
    }
    /** Body of kModalDialog._CancelClick (moved verbatim; the dialog is Dlg). */
    public static _CancelClick(Dlg: kModalDialog): void {
        if (ks.KErrorMode && ks.KShowingErrorDialog === false) { return; }
        try {
            if (Dlg.OnCancelCB) {
                ks.DoCallBack(Dlg.OnCancelCB, false);
            }
            Dlg.Close();
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogButtons._CancelClick"); }
    }
    /** Body of kModalDialog.AddOKButton (moved verbatim; the dialog is Dlg). */
    public static AddOKButton(Dlg: kModalDialog, Callback?: any): void {
        if (ks.KErrorMode && ks.KShowingErrorDialog === false) { return; }
        try {
            // Idempotent (see AddCancelButton): avoid a duplicate OK button
            // when both the constructor and Show() run the lazy build.
            if (Dlg.OKButton !== null) {
                if (Callback) { Dlg.OKCB = Callback; }
                return;
            }
            // Empty class name behaves like an unset one (see AddCancelButton), so the
            // default look is unchanged unless a caller opts in.
            const Ok: khtmlButton = new khtmlButton(
                "kDialogOKButton",
                Dlg.OKButtonClassName,
                Dlg.ButtonsUseDefaultStyle
            );
            Dlg.OKButton = Ok;
            if (Ok.Button) {
                // The 2px border is part of the built-in look; when a host stylesheet owns
                // the button (ButtonsUseDefaultStyle=false) it decides its own border.
                if (Dlg.ButtonsUseDefaultStyle === true) {
                    // OK is THE confirming action, so it gets the filled accent. It used
                    // to be styled identically to Cancel (both plain, both 2px), which
                    // meant the user had to read the labels to find the safe one.
                    Ok.SetPrimary();
                }
                const Row: HTMLDivElement = Dlg["_GetButtonRow"]();
                Row.appendChild(Ok.Button);
                if (Callback) {
                    Dlg.OKCB = Callback;
                }
                if (ks.Nz(Dlg.OKButtonCaption) !== "") {
                    Ok.SetText(Dlg.OKButtonCaption);
                } else {
                    Ok.SetText(LNG.OK);
                }
                Ok.SetCallBack(Dlg["_OKClick"]);
            }
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogButtons.AddOKButton"); }
    }
    /** Body of kModalDialog._OKClick (moved verbatim; the dialog is Dlg). */
    public static _OKClick(Dlg: kModalDialog): void {
        if (ks.KErrorMode && ks.KShowingErrorDialog === false) { return; }
        try {
            if (Dlg.OKValidateCB !== null && Dlg.OKValidateCB() === false) {
                return; // validation rejected — keep the dialog open, do not commit
            }
            if (Dlg.OKCB) {
                ks.DoCallBack(Dlg.OKCB,true);
            }
            if (Dlg.KeepOpenOnOK === false) { Dlg.Close(); }
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogButtons._OKClick"); }
    }
    /** Body of kModalDialog.CancelDialog (moved verbatim; the dialog is Dlg). */
    public static CancelDialog(Dlg: kModalDialog): void {
        if (ks.KErrorMode && ks.KShowingErrorDialog === false) { return; }
        try {
            Dlg.Close();
            if (Dlg.OnCancelCB){
                ks.DoCallBack(Dlg.OnCancelCB, null, false);
            }
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogButtons.CancelDialog"); }
    }
    /** Body of kModalDialog.SetCancelButton (moved verbatim; the dialog is Dlg). */
    public static SetCancelButton(Dlg: kModalDialog): void {
        if (ks.KErrorMode === true) { return; }
        try {
            // The button is built lazily in AddCancelButton; nothing to
            // configure until it exists.
            const Cancel: khtmlButton | null = Dlg.CancelButton;
            if (Cancel === null) { return; }
            if(Dlg["_ShowCancelButton"]){
                if("" !== Dlg.CancelButtonCaption){
                    Cancel.SetText(Dlg.CancelButtonCaption);
                }else{
                    Cancel.SetText(LNG.CANCEL);
                }
                if(Dlg.OnCancelCB){
                    Cancel.SetCallBack(Dlg.OnCancelCB);
                }else{
                    Cancel.SetCallBack(Dlg.Close);
                }
            }else{
                Cancel.Button.style.display = "none";
            }
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogButtons.SetCancelButton"); }
    }
    /** Body of kModalDialog.SetCancelCaption (moved verbatim; the dialog is Dlg). */
    public static SetCancelCaption(Dlg: kModalDialog, Caption: string): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Dlg.CancelButtonCaption = Caption;
            if (Dlg.CancelButton === null) { return; }
            if (Caption !== "") {
                Dlg.CancelButton.SetText(Caption);
            } else {
                Dlg.CancelButton.SetText(LNG.CANCEL);
            }
        } catch (e) { ks.KGlobalErrorHandler(e, "kModalDialogButtons.SetCancelCaption"); }
    }
}
